import { bankDecisionAnswerReadSchema, ownFileSchema, type OwnFile } from "@hakili/contracts";
import { oneDecimalLabel } from "@hakili/domain-types";
import { formatDateTime, formatMoney, t } from "@hakili/i18n";
import {
  ActionBar,
  BANK_FS_BODY,
  BANK_FS_META,
  BANK_TEXT_2,
  Button,
  Callout,
  Chip,
  GuardedButton,
  Modal,
  NumberStepper,
  SegmentedControl,
  StatCard,
  Textarea,
} from "@hakili/ui";
import { RefuseModal } from "./revue.js";
import { AlertTriangle, ChevronRight, Info, Lock, Send } from "lucide-react";
import { type ReactElement, useEffect, useState } from "react";

import { gradeFrom, type AnalysisGradeView, type AnalysisOpinionView } from "./analyse.js";
import {
  type CommitteeFileView,
  type CommitteeRead,
  CommitteesToRecord,
  type RecordingFile,
  committeeFilesFrom,
  committeeReadFrom,
} from "./comite.js";
import { GUARD_CHIP } from "./d2-parts.js";
import { DecisionHead, DecisionJustification, DecisionTiles } from "./decision-head.js";
import { axisFrom, PolicyCard, type RateAxisView } from "./decision-policy.js";
import { DASH, fill } from "./dossiers.js";
import { ModalCancel } from "./modal-cancel.js";

/** A stamp the door answered, in French; a dash on a stamp that does not parse (R81), never a throw. */
export const when = (iso: string): string =>
  Number.isNaN(Date.parse(iso)) ? DASH : formatDateTime(new Date(iso));

/**
 * Round `L-1b`, `M4` , **the chef's two screens to the drawing**
 * (`hakili-bank.jsx`, `DecisionsScreen` and `DecisionScreen`; WF-BNK-20,
 * WF-BNK-21; `P1-BKW-013` in its phase 1 single-file form).
 *
 * « Décisions » is the chef's landing: the four indicators derived from the
 * queue, the filters, the rows with their chips (« reçue de votre analyste à
 * l'instant » on a fresh one, the wait in rose past 24 hours), « Décisions
 * récentes » from the decisions taken. Bulk validation is ABSENT and its
 * control is not rendered (`P1-BKW-013`'s test); a row carrying a gap shows
 * the drawing's lock, which is a mark and not a control. The committee
 * card's counsel and sessions, « Points d'attention dans l'équipe » and « Interroger Hakili » are NOT
 * built (D3 §4, D-1020-9, D-1019-2): they are not drawn, greyed or dashed. `COMITE-RECORD-2` reads the committee card's FILES
 * and draws them as « Comités à enregistrer » (frame 110).
 *
 * The decision screen: the header with the grade (a dash, R81) and the
 * delegation chip; « Ce qui vous est demandé »; « Écarts signalés avant
 * l'envoi »; the analyst's justification signed and dated; « Arbitrage
 * commercial » with the interest income derived at the retained rate and the
 * probability a dash; « Éclairage de Hakili » greyed with its round; the
 * right column , the file in brief, the analyst's indicators (his own or a
 * dash, never borrowed), « Question à l'analyste » greyed (L-3), the
 * portfolio effect a dash; the controls « Refuser », « Renvoyer »,
 * « Ajuster le taux », « Valider et envoyer au client », « Accorder sous
 * condition » greyed (the warranty is not built). Every decision's words are
 * the drawing's; a control's door is the chef's own.
 */
export interface QueueRowView {
  readonly id: string;
  readonly offerId: string;
  readonly reference: string;
  readonly clientName: string | null;
  readonly analystName: string | null;
  readonly amountFinanced: number;
  readonly durationMonths: number;
  readonly nominalRatePct: string;
  readonly instalmentAmount: number;
  readonly taegPct: string;
  readonly gridRatePct: string | null;
  readonly gaps: readonly {
    readonly code: string;
    readonly rule: string;
    readonly detail: string;
    readonly level: string;
  }[];
  readonly requiredLevel: string | null;
  readonly justification: string;
  readonly submittedAt: string | null;
  readonly waitHours: number | null;
  readonly fresh: boolean;
  readonly late: boolean;
  readonly delegationThresholdAmount: number | null;
  readonly requiresCommittee: boolean;
  readonly stage: string;
}

export interface DecisionTakenView {
  readonly id: string;
  readonly fileId: string;
  readonly reference: string;
  readonly clientName: string | null;
  readonly outcome: "validee" | "renvoyee" | "refusee";
  readonly ratePct: string | null;
  readonly rateAdjusted: boolean;
  readonly note: string | null;
  readonly decidedByName: string | null;
  readonly decidedAt: string;
  readonly instructions: readonly {
    readonly id: string;
    readonly text: string;
    readonly treatedAt: string | null;
  }[];
  /** `CHEF-SELF-VALIDATION` 2c: « Instruit et validé par le chef du crédit », served on a decision the chef took on the file she instructed herself; `null` or absent otherwise. Drawn as served. */
  readonly journalFact?: string | null;
}

export interface QueueView {
  readonly pending: readonly QueueRowView[];
  readonly recent: readonly DecisionTakenView[];
  /** `COMITE-RECORD-2` (frame 110): the files at the comité, each with the reasons the server kept. */
  readonly committee: readonly CommitteeFileView[];
  readonly indicators: {
    readonly pendingCount: number;
    readonly amountAtStakeAmount: number;
    readonly averageDelayHours: number | null;
    readonly conformCount: number;
    readonly lateCount: number;
  };
}

/** `#772`: a gap that stands on no table row, served as one more `controls[]` entry (D2 frame 170); the browser draws it and counts nothing. */
export interface DecisionGapRowView {
  readonly code: string;
  readonly label: string;
  readonly proposed: string;
  readonly limit: string;
  readonly level: "comite" | "chef";
}

export type DecisionControlState = "ok" | "ecart" | "bloquant";
export interface DecisionControlView {
  readonly code: string;
  readonly proposed: number | string;
  readonly policy: Readonly<Record<string, number | string>>;
  readonly state: DecisionControlState;
  /** `CHEF-AXIS` (62b-S): on the rate control, and only on an `ecart`, the gap to the grid in points, as the door serves it. */
  readonly gapPts?: number;
}

/** The decision screen's facts: the queue row with the recap the door derived. */
export interface DecisionFileView extends QueueRowView {
  readonly facts: {
    readonly depositPct: number | null;
    readonly expertisedValueAmount: number | null;
    readonly hakiliFeeAmount: number;
    readonly bankFeeAmount: number;
    readonly capacityAmount: number;
    /** The platform's standing insurance rate, from the door: the interest figure moves with the slider on it. */
    readonly borrowerInsuranceRatePct: number;
  };
  readonly recap: {
    readonly instalmentAmount: number;
    readonly taegPct: string;
    readonly totalFinancedAmount: number;
  };
  readonly gridComparable: boolean;
  /** `D2` frame 62: the controls the door derives from the composer and the stored policy; a row with no policy value is absent. */
  readonly controls: readonly DecisionControlView[];
  readonly rateTolerancePct: string | null;
  /** `#772`: the gaps with no control row, after « Montant » (served order). */
  readonly gapRows?: readonly DecisionGapRowView[];
  /** `#772`: D2's words for why the committee sits after the client's agreement; the « Montant » chip reads them. */
  readonly committeeReasons?: readonly { readonly code: string; readonly words: string }[];
  /** `#772`: the head's « {n} écart », served. */
  readonly gapCount?: number | null;
  /** `CHEF-AXIS` (62b-S): the axis under « Le taux face à la politique », positions and words served; `null` where the door serves none. */
  readonly axis: RateAxisView | null;
  readonly vehicleLabel: string | null;
  /** `COMITE-RECORD-2`: the reasons kept at the acceptance and the sittings recorded; `null` for a file never at the comité. */
  readonly committee: CommitteeRead | null;
  readonly decisions: readonly DecisionTakenView[];
  /** `CHEF-SELF-VALIDATION` 2c: the chef who instructed this file herself, the route and sentence the server serves with its `notice` (« Vous avez instruit ce dossier. »); `null` for anyone else. */
  readonly ownFile?: OwnFile | null;
  /** `L-3b`, `M1` (WF-BNK-08): the closed list the chef's modal offers, the door's own. */
  readonly refusalReasons: readonly { readonly code: string; readonly label: string }[];
  /** `U-2`, `M3` (WF-BNK-20 step 1): the grade and the avis where the drawing places them; `null` with no run (the dash). */
  readonly analysis: {
    readonly grade: AnalysisGradeView | null;
    readonly opinion: AnalysisOpinionView | null;
  } | null;
}

/**
 * `COMMITTEE-AMOUNT` (D-1025-9): the file the form is given from the decision read. « Montant financé » is the offer's TOTAL financed amount (the vehicle
 * part and the fees the bank agreed to carry: the quantity the threshold was compared to), the figure the reasons' sentence prints , one figure on one screen.
 */
export const recordingFileOf = (read: DecisionFileView): RecordingFile => ({
  reference: read.reference,
  clientName: read.clientName,
  amountFinanced: read.recap.totalFinancedAmount,
  durationMonths: read.durationMonths,
  nominalRatePct: read.nominalRatePct,
  delegationThresholdAmount: read.delegationThresholdAmount,
  stage: read.stage,
  committee: read.committee,
});

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;
const str = (value: unknown): string | null => (typeof value === "string" ? value : null);
const num = (value: unknown): number | null => (typeof value === "number" ? value : null);

/**
 * `AJUSTER-TAUX` (lane AA, `POST /api/v1/bank/decisions/{id}/preview`): the composer's own result at the rate the chef is about to pick.
 * The screen prints it and computes none of it: a block or a gap the browser did not read from this answer does not exist here.
 */
export interface RatePreview {
  readonly instalmentAmount: number;
  readonly taegPct: string;
  readonly blocks: readonly { readonly code: string; readonly rule: string; readonly detail: string }[];
  readonly gaps: readonly {
    readonly code: string;
    readonly rule: string;
    readonly detail: string;
    readonly level: string;
  }[];
  readonly justificationRequired: boolean;
}

export function ratePreviewFrom(raw: unknown): RatePreview | null {
  const body = asRecord(raw);
  const instalment = num(body?.["instalmentAmount"] ?? null);
  const taeg = str(body?.["taegPct"] ?? null);
  if (body === null || instalment === null || taeg === null) return null;
  const rows = (value: unknown): Record<string, unknown>[] =>
    Array.isArray(value)
      ? value.map(asRecord).filter((one): one is Record<string, unknown> => one !== null)
      : [];
  return {
    instalmentAmount: instalment,
    taegPct: taeg,
    blocks: rows(body["blocks"]).map((one) => ({
      code: str(one["code"]) ?? "",
      rule: str(one["rule"]) ?? "",
      detail: str(one["detail"]) ?? "",
    })),
    gaps: rows(body["gaps"]).map((one) => ({
      code: str(one["code"]) ?? "",
      rule: str(one["rule"]) ?? "",
      detail: str(one["detail"]) ?? "",
      level: str(one["level"]) ?? "",
    })),
    justificationRequired: body["justificationRequired"] === true,
  };
}

export function queueRowFrom(raw: unknown): QueueRowView | null {
  const row = asRecord(raw);
  if (row === null || typeof row["id"] !== "string") return null;
  const gaps = Array.isArray(row["gaps"])
    ? row["gaps"]
        .map(asRecord)
        .filter((one): one is Record<string, unknown> => one !== null)
        .map((one) => ({
          code: str(one["code"]) ?? "",
          rule: str(one["rule"]) ?? "",
          detail: str(one["detail"]) ?? "",
          level: str(one["level"]) ?? "",
        }))
    : [];
  return {
    id: row["id"],
    offerId: str(row["offerId"]) ?? "",
    reference: str(row["reference"]) ?? "",
    clientName: str(row["clientName"]),
    analystName: str(row["analystName"]),
    amountFinanced: num(row["amountFinanced"]) ?? 0,
    durationMonths: num(row["durationMonths"]) ?? 0,
    nominalRatePct: str(row["nominalRatePct"]) ?? "",
    instalmentAmount: num(row["instalmentAmount"]) ?? 0,
    taegPct: str(row["taegPct"]) ?? "",
    gridRatePct: str(row["gridRatePct"]),
    gaps,
    requiredLevel: str(row["requiredLevel"]),
    justification: str(row["justification"]) ?? "",
    submittedAt: str(row["submittedAt"]),
    waitHours: num(row["waitHours"]),
    fresh: row["fresh"] === true,
    late: row["late"] === true,
    delegationThresholdAmount: num(row["delegationThresholdAmount"]),
    requiresCommittee: row["requiresCommittee"] === true,
    stage: str(row["stage"]) ?? "",
  };
}

export function decisionTakenFrom(raw: unknown): DecisionTakenView | null {
  const row = asRecord(raw);
  if (row === null || typeof row["id"] !== "string") return null;
  const outcome = row["outcome"];
  if (outcome !== "validee" && outcome !== "renvoyee" && outcome !== "refusee") return null;
  return {
    id: row["id"],
    fileId: str(row["fileId"]) ?? "",
    reference: str(row["reference"]) ?? "",
    clientName: str(row["clientName"]),
    outcome,
    ratePct: str(row["ratePct"]),
    rateAdjusted: row["rateAdjusted"] === true,
    note: str(row["note"]),
    decidedByName: str(row["decidedByName"]),
    journalFact: str(row["journalFact"]),
    decidedAt: str(row["decidedAt"]) ?? "",
    instructions: Array.isArray(row["instructions"])
      ? row["instructions"]
          .map(asRecord)
          .filter((one): one is Record<string, unknown> => one !== null)
          .map((one) => ({
            id: str(one["id"]) ?? "",
            text: str(one["text"]) ?? "",
            treatedAt: str(one["treatedAt"]),
          }))
      : [],
  };
}

export function queueFrom(answer: unknown): QueueView {
  const body = asRecord(answer);
  const indicators = asRecord(body?.["indicators"]);
  return {
    pending: Array.isArray(body?.["pending"])
      ? body["pending"].map(queueRowFrom).filter((one): one is QueueRowView => one !== null)
      : [],
    recent: Array.isArray(body?.["recent"])
      ? body["recent"].map(decisionTakenFrom).filter((one): one is DecisionTakenView => one !== null)
      : [],
    committee: committeeFilesFrom(body?.["committee"]),
    indicators: {
      pendingCount: num(indicators?.["pendingCount"]) ?? 0,
      amountAtStakeAmount: num(indicators?.["amountAtStakeAmount"]) ?? 0,
      averageDelayHours: num(indicators?.["averageDelayHours"]),
      conformCount: num(indicators?.["conformCount"]) ?? 0,
      lateCount: num(indicators?.["lateCount"]) ?? 0,
    },
  };
}

const RECOMMENDATIONS = ["favorable", "reserve", "defavorable"] as const;

/** The chef's read of the analysis: the grade with its components, the avis with the analyst's act beside. */
function analysisOfDecision(raw: unknown): DecisionFileView["analysis"] {
  const view = asRecord(raw);
  if (view === null) return null;
  const grade = asRecord(view["grade"]);
  const opinion = asRecord(view["opinion"]);
  const act = asRecord(opinion?.["act"]);
  const actKind = str(act?.["act"]);
  const recommendation = RECOMMENDATIONS.find((one) => one === opinion?.["recommendation"]);
  return {
    grade: gradeFrom(grade),
    opinion:
      opinion === null || str(opinion["text"]) === null || recommendation === undefined
        ? null
        : {
            text: str(opinion["text"]) ?? "",
            recommendation,
            notice: str(opinion["notice"]),
            act:
              act === null ||
              (actKind !== "confirmee" && actKind !== "corrigee") ||
              str(act["byName"]) === null
                ? null
                : {
                    act: actKind,
                    value: str(act["value"]),
                    byName: str(act["byName"]) ?? "",
                    actedAt: str(act["actedAt"]) ?? "",
                    screening: null,
                  },
          },
  };
}

function gapRowFrom(raw: unknown): DecisionGapRowView | null {
  const one = asRecord(raw);
  const code = str(one?.["code"]);
  const label = str(one?.["label"]);
  const proposed = str(one?.["proposed"]);
  const limit = str(one?.["limit"]);
  const level = one?.["level"];
  if (code === null || label === null || proposed === null || limit === null) return null;
  if (level !== "comite" && level !== "chef") return null;
  return { code, label, proposed, limit, level };
}

function controlFrom(raw: unknown): DecisionControlView | null {
  const one = asRecord(raw);
  const code = str(one?.["code"]);
  const state = one?.["state"];
  const proposed = one?.["proposed"];
  const policy = asRecord(one?.["policy"]);
  if (code === null || policy === null) return null;
  if (state !== "ok" && state !== "ecart" && state !== "bloquant") return null;
  if (typeof proposed !== "number" && typeof proposed !== "string") return null;
  const values: Record<string, number | string> = {};
  for (const [key, value] of Object.entries(policy)) {
    if (typeof value === "number" || typeof value === "string") values[key] = value;
  }
  const gapPts = num(one?.["gapPts"]);
  return { code, proposed, policy: values, state, ...(gapPts === null ? {} : { gapPts }) };
}

/**
 * `PARSER-GAPCOUNT` (A47, A48): the decision door's answer, read with the contract's LENIENT twin (`bankDecisionAnswerReadSchema`, `@hakili/contracts`): a surplus key is tolerated at every level, a missing required field (`gapCount`
 * among them) or a wrong type fails, and then there is no file: the page says so by name, it never draws a half-read answer nor falls back to a count of its own.
 */
export type DecisionRead = { readonly ok: true; readonly file: DecisionFileView } | { readonly ok: false };

/**
 * The decision page before it has a file: loading, or, when the door's answer broke its contract, the named state. D2's failure pattern (frames 04 and 210; the designer's review): the page keeps its own frame (the breadcrumb
 * « File de décision › {reference} » with the way back, the route's name as the title, from the catalogue the routes already use) and the failure is the WARN callout with the alert icon, « Réessayer » inside it, under `role="alert"`.
 */
export function DecisionPending(props: {
  readonly broken: boolean;
  readonly onRetry: () => void;
  readonly onBack: () => void;
  readonly reference: string | null;
  readonly route: "decision" | "comite";
}): ReactElement {
  if (!props.broken) return <p className="p-6 text-sm text-gray-500">{t("bank.session.chargement")}</p>;
  return (
    <div data-testid="bank-decision-contrat-rompu" role="alert">
      <div
        className={`mb-3 flex items-center gap-2 ${BANK_FS_BODY} ${BANK_TEXT_2}`}
        data-testid="bank-decision-fil"
      >
        <button
          type="button"
          onClick={props.onBack}
          className="hover:text-[color:var(--hk-brand-strong)]"
          data-testid="bank-decision-retour"
        >
          {t("bank.decision.fileRetour")}
        </button>
        {props.reference === null ? null : (
          <>
            <ChevronRight className="h-3 w-3" aria-hidden="true" />
            <span className="font-semibold">{props.reference}</span>
          </>
        )}
      </div>
      <h1
        className="mb-4 mt-[14px] text-[length:var(--hk-fs-h1)] font-semibold leading-[var(--hk-lh-tight)] tracking-[-.01em]"
        data-testid="bank-decision-titre-rompu"
      >
        {t(props.route === "comite" ? "bank.comite.filCourant" : "bank.decisions.typeOffre")}
      </h1>
      <Callout tone="warn" icon={<AlertTriangle className="h-5 w-5" aria-hidden="true" />}>
        <p className="m-0">{t("bank.decision.contratRompu")}</p>
        <p className="mb-0 mt-3">
          <Button variant="secondary" size="sm" onClick={props.onRetry} data-testid="bank-decision-reessayer">
            {t("common.action.reessayer")}
          </Button>
        </p>
      </Callout>
    </div>
  );
}

export function readDecisionAnswer(answer: unknown): DecisionRead {
  const parsed = bankDecisionAnswerReadSchema.safeParse(answer);
  if (!parsed.success) return { ok: false };
  const file = decisionFileFrom(parsed.data);
  return file === null ? { ok: false } : { ok: true, file };
}

export function decisionFileFrom(answer: unknown): DecisionFileView | null {
  const row = queueRowFrom(answer);
  const body = asRecord(answer);
  if (row === null || body === null) return null;
  const facts = asRecord(body["facts"]);
  const recap = asRecord(body["recap"]);
  return {
    ...row,
    facts: {
      depositPct: num(facts?.["depositPct"]),
      expertisedValueAmount: num(facts?.["expertisedValueAmount"]),
      hakiliFeeAmount: num(facts?.["hakiliFeeAmount"]) ?? 0,
      bankFeeAmount: num(facts?.["bankFeeAmount"]) ?? 0,
      capacityAmount: num(facts?.["capacityAmount"]) ?? 0,
      borrowerInsuranceRatePct: num(facts?.["borrowerInsuranceRatePct"]) ?? 0,
    },
    recap: {
      instalmentAmount: num(recap?.["instalmentAmount"]) ?? row.instalmentAmount,
      taegPct: str(recap?.["taegPct"]) ?? row.taegPct,
      totalFinancedAmount: num(recap?.["totalFinancedAmount"]) ?? row.amountFinanced,
    },
    gridComparable: body["gridComparable"] === true,
    controls: Array.isArray(body["controls"])
      ? body["controls"].map(controlFrom).filter((one): one is DecisionControlView => one !== null)
      : [],
    gapRows: Array.isArray(body["controls"])
      ? body["controls"].map(gapRowFrom).filter((one): one is DecisionGapRowView => one !== null)
      : [],
    committeeReasons: Array.isArray(body["committeeReasons"])
      ? body["committeeReasons"].flatMap((one) => {
          const row = asRecord(one);
          const code = str(row?.["code"]);
          const words = str(row?.["words"]);
          return code === null || words === null ? [] : [{ code, words }];
        })
      : [],
    gapCount: num(body["gapCount"]),
    rateTolerancePct: str(body["rateTolerancePct"]),
    axis: axisFrom(body["axis"]),
    vehicleLabel: str(body["vehicleLabel"]),
    ownFile: ((): OwnFile | null => {
      const parsed = ownFileSchema.nullable().safeParse(body["ownFile"]);
      return parsed.success ? parsed.data : null;
    })(),
    committee: committeeReadFrom(body["committee"]),
    analysis: analysisOfDecision(body["underwriting"]),
    decisions: Array.isArray(body["decisions"])
      ? body["decisions"].map(decisionTakenFrom).filter((one): one is DecisionTakenView => one !== null)
      : [],
    refusalReasons: Array.isArray(body["refusalReasons"])
      ? body["refusalReasons"].flatMap((one) => {
          const row = asRecord(one);
          const code = str(row?.["code"]);
          const label = str(row?.["label"]);
          return code === null || label === null ? [] : [{ code, label }];
        })
      : [],
  };
}

const rateLabel = (rate: string): string => rate.replace(".", ",");
/** The drawing's « 5,5 M »: one decimal, the French comma. */
const millions = (amount: number): string =>
  fill("bank.decisions.millions", { n: rateLabel(oneDecimalLabel(amount / 1_000_000)) });

/** The wait the drawing prints beside a row: « à l'instant », « il y a N heures », « il y a N jours ». */
export function waitLabel(row: Pick<QueueRowView, "fresh" | "waitHours">): string {
  if (row.fresh) return t("bank.decisions.aLInstant");
  if (row.waitHours === null) return DASH;
  const days = (row.waitHours - (row.waitHours % 24)) / 24;
  if (days >= 1) return fill("bank.decisions.attenteJours", { n: String(days) });
  return fill("bank.decisions.attenteHeures", { n: String(row.waitHours) });
}

/** The row's one-line reading: the term, the rate, and its standing to the policy. */
export const resumeOf = (row: QueueRowView): string =>
  row.gaps.length === 0
    ? fill("bank.decisions.resumeConforme", {
        mois: String(row.durationMonths),
        taux: rateLabel(row.nominalRatePct),
      })
    : fill("bank.decisions.resumeEcart", {
        mois: String(row.durationMonths),
        taux: rateLabel(row.nominalRatePct),
        n: String(row.gaps.length),
      });

type Filter = "toutes" | "conformes" | "ecarts" | "retard";
const FILTERS: readonly { readonly id: Filter; readonly key: string }[] = [
  { id: "toutes", key: "bank.decisions.filtreToutes" },
  { id: "conformes", key: "bank.decisions.filtreConformes" },
  { id: "ecarts", key: "bank.decisions.filtreEcarts" },
  { id: "retard", key: "bank.decisions.filtreRetard" },
];

export function Decisions(props: {
  readonly fullName: string;
  readonly queue: QueueView;
  readonly onOpen: (fileId: string) => void;
  /** `COMITE-RECORD-2`: a row of « Comités à enregistrer » opens `/comite/:reference`. */
  readonly onCommittee: (reference: string) => void;
}): ReactElement {
  const [filter, setFilter] = useState<Filter>("toutes");
  const { pending, indicators } = props.queue;
  const visible = pending.filter((row) =>
    filter === "toutes"
      ? true
      : filter === "conformes"
        ? row.gaps.length === 0
        : filter === "ecarts"
          ? row.gaps.length > 0
          : row.late,
  );
  const tiles: readonly {
    readonly testId: string;
    readonly label: string;
    readonly value: string;
    readonly sub: string;
    readonly ok?: boolean;
  }[] = [
    {
      testId: "bank-decisions-indic-attente",
      label: t("bank.decisions.indicAttente"),
      value: String(indicators.pendingCount),
      sub: t("bank.decisions.indicAttenteSous"),
    },
    {
      testId: "bank-decisions-indic-montant",
      label: t("bank.decisions.indicMontant"),
      value: millions(indicators.amountAtStakeAmount),
      sub: t("bank.decisions.indicMontantSous"),
    },
    {
      testId: "bank-decisions-indic-delai",
      label: t("bank.decisions.indicDelai"),
      value:
        indicators.averageDelayHours === null
          ? t("bank.feuille.vide")
          : fill("bank.decisions.heures", { n: rateLabel(oneDecimalLabel(indicators.averageDelayHours)) }),
      sub: t("bank.decisions.indicDelaiSous"),
      // « ok » says a measured figure is settled; the empty sign is a dash, not a verdict, and takes the tile's own ink (designer's re-look f259a5c0).
      ok: indicators.averageDelayHours !== null,
    },
    {
      testId: "bank-decisions-indic-conformes",
      label: t("bank.decisions.indicConformes"),
      value: fill("bank.decisions.surN", {
        n: String(indicators.conformCount),
        total: String(pending.length),
      }),
      sub: t("bank.decisions.indicConformesSous"),
    },
  ];
  return (
    <div data-testid="bank-decisions">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">{t("bank.nav.decisions")}</h1>
        {/* D2 (110): ONE sentence, full stops: « {n} décision(s) en attente. {conformité} {m} comité(s) à enregistrer : … ». */}
        <p className="mt-1.5 text-sm text-gray-500" data-testid="bank-decisions-phrase">
          {indicators.pendingCount === 0 ? (
            t("bank.decisions.rienAttend")
          ) : (
            <>
              <strong className="font-semibold text-gray-900">
                {fill(
                  indicators.pendingCount > 1 ? "bank.decisions.enAttentePlural" : "bank.decisions.enAttente",
                  {
                    n: String(indicators.pendingCount),
                  },
                )}
                .
              </strong>{" "}
              {indicators.conformCount >= indicators.pendingCount
                ? t("bank.decisions.toutesConformes")
                : fill(
                    indicators.pendingCount - indicators.conformCount > 1
                      ? "bank.decisions.plusieursEcarts"
                      : "bank.decisions.unEcart",
                    { n: String(indicators.pendingCount - indicators.conformCount) },
                  )}
            </>
          )}
          {props.queue.committee.length === 0 ? null : " "}
          {props.queue.committee.length === 0 ? null : (
            <span data-testid="bank-decisions-comites-phrase">
              <strong className="font-semibold text-gray-900">
                {fill(
                  props.queue.committee.length > 1
                    ? "bank.decisions.comitesPhrasePluriel"
                    : "bank.decisions.comitesPhrase",
                  { n: String(props.queue.committee.length) },
                )}
              </strong>{" "}
              {t("bank.decisions.comitesPhraseFin")}
            </span>
          )}
        </p>
        {props.queue.committee.length === 0 ? null : (
          <p className="mt-1 text-sm text-gray-500" data-testid="bank-decisions-comites-phrase">
            {fill(
              props.queue.committee.length > 1
                ? "bank.decisions.comitesPhrasePluriel"
                : "bank.decisions.comitesPhrase",
              { n: String(props.queue.committee.length) },
            )}
          </p>
        )}
      </div>

      <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="bank-decisions-indicateurs">
        {tiles.map((tile) => (
          <div key={tile.testId} data-testid={tile.testId}>
            <StatCard
              label={tile.label}
              value={tile.value}
              hint={tile.sub}
              {...(tile.ok === true ? { tone: "ok" as const } : {})}
            />
          </div>
        ))}
      </div>

      <div className="mb-3" data-testid="bank-decisions-filtres">
        <SegmentedControl
          label={t("bank.decisions.filtresNom")}
          value={filter}
          options={FILTERS.map((one) => ({ value: one.id, label: t(one.key) }))}
          onChange={(value) => {
            setFilter(value as Filter);
          }}
        />
      </div>

      <div className="mb-3 overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <ul className="divide-y divide-gray-100" data-testid="bank-decisions-file">
          {visible.map((row) => {
            const conform = row.gaps.length === 0;
            return (
              <li
                key={row.id}
                className="flex items-center gap-3 px-5 py-4 transition-colors hover:bg-gray-50"
              >
                {/* A fixed gutter, so the rows align whether or not one carries a lock; nothing is drawn for a conforming row. */}
                <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center">
                  {conform ? null : (
                    <Lock
                      className="h-4 w-4 text-gray-500"
                      aria-label={t("bank.decisions.ecartIndividuel")}
                      role="img"
                      data-testid={`bank-decision-verrou-${row.id}`}
                    />
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    props.onOpen(row.id);
                  }}
                  className="flex min-w-0 flex-1 cursor-pointer flex-wrap items-center gap-x-4 gap-y-1 text-left"
                  data-testid={`bank-decision-ligne-${row.id}`}
                >
                  <span className="w-36 shrink-0">
                    <Chip tone={conform ? "neutre" : "attention"}>
                      {t(conform ? "bank.decisions.typeOffre" : "bank.decisions.typeDerogation")}
                    </Chip>
                  </span>
                  <span className="w-44 min-w-0 shrink">
                    <span className="block text-sm font-medium text-gray-900">{row.clientName ?? DASH}</span>
                    <span className={`mt-0.5 block ${BANK_FS_META} text-gray-500`}>
                      {row.reference} · {row.analystName ?? DASH}
                    </span>
                  </span>
                  <span className="w-28 shrink-0 text-right text-sm font-medium tabular-nums">
                    {formatMoney(row.amountFinanced)}
                  </span>
                  <span className="flex-1 truncate text-sm text-gray-500">{resumeOf(row)}</span>
                  {row.fresh ? (
                    <span
                      className={`shrink-0 ${BANK_FS_BODY} ${BANK_TEXT_2}`}
                      data-testid={`bank-decision-fraiche-${row.id}`}
                    >
                      {t("bank.decisions.recueInstant")}
                    </span>
                  ) : null}
                  <span
                    className={`w-24 shrink-0 text-right ${BANK_FS_META} ${
                      !row.fresh && row.late ? "font-medium text-rose-700" : "text-gray-500"
                    }`}
                  >
                    {waitLabel(row)}
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
        {visible.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-gray-500" data-testid="bank-decisions-vide">
            {t("bank.decisions.aucune")}
          </p>
        ) : null}
      </div>

      <CommitteesToRecord files={props.queue.committee} onOpen={props.onCommittee} />
    </div>
  );
}

export function Row(props: {
  readonly label: string;
  readonly value: string;
  readonly strong?: boolean;
  readonly negative?: boolean;
  readonly testId?: string;
}): ReactElement {
  return (
    <div className="flex items-center justify-between py-1.5" data-testid={props.testId}>
      <span className="text-sm text-gray-500">{props.label}</span>
      <span
        className={
          "text-sm tabular-nums " +
          (props.negative === true
            ? "font-medium text-rose-600"
            : props.strong === true
              ? "font-semibold text-gray-900"
              : "text-gray-900")
        }
      >
        {props.value}
      </span>
    </div>
  );
}

/** The body the chef's door takes (`POST /bank/decisions/{id}`). */
export interface DecisionBody {
  readonly outcome: "validee" | "renvoyee" | "refusee";
  readonly ratePct?: string;
  readonly note?: string;
  readonly instructions?: readonly string[];
  readonly reasonCode?: string;
  /** `DECISION-SEND-REFERENCE`: the reference the screen printed; the door refuses (409) when it is not the file's. Set by the shell, never typed. */
  readonly seenReference?: string;
}

const RATE_MIN = 10;
const RATE_MAX = 12.5;

export function Decision(props: {
  readonly file: DecisionFileView;
  /** `POLICY-FIRST` Screen B (frame 167): the bank has no credit policy (`GET /bank/policy` answered `policy: null`); the validation is guarded. */
  readonly policyMissing?: boolean;
  readonly borrowerInsuranceRatePct: number;
  readonly busy: boolean;
  readonly refusal: string | null;
  readonly onBack: () => void;
  /** `D2` frame 62's sub-line: the chef's own bank, from his session; `null` or absent leaves it out. */
  readonly bankName?: string | null;
  /** The offer door's `facts.vehicleYear` (`ARC-3`'s source); `null` or absent leaves the year out. */
  readonly vehicleYear?: number | null;
  /** The file door's `durationMonths`, the term asked (`ARC-3`'s source); `null` or absent leaves « {n} mois demandés » out. */
  readonly requestedMonths?: number | null;
  readonly onDecide: (body: DecisionBody) => void;
  /** `S1-a` COMPORTEMENT DU COMPTE (`D2-behaviour.md` §12): the button « Comportement du compte » under the head, to the file's behaviour page; absent, no button is drawn. */
  readonly onComportement?: () => void;
  /** `AJUSTER-TAUX`: the door's answer at a rate (`null` when it did not answer). Absent where a screen draws no window. */
  readonly preview?: (ratePct: string) => Promise<RatePreview | null>;
}): ReactElement {
  const { file } = props;
  const [panel, setPanel] = useState<"none" | "renvoyer" | "ajuster">("none");
  // `L-3b`: « Refuser » opens the drawing's modal (revue.tsx's `RefuseModal`), remounted at each opening.
  const [refuseOpen, setRefuseOpen] = useState<number>(0);
  const [rate, setRate] = useState(Number(file.nominalRatePct));
  const [note, setNote] = useState("");
  const [instructions, setInstructions] = useState("");
  const rateText = oneDecimalLabel(rate);
  // `AJUSTER-TAUX`: what the door answered for the rate on the stepper; `null` until it does (a stale answer is never kept).
  const [answered, setAnswered] = useState<{
    readonly rate: string;
    readonly value: RatePreview | null;
  } | null>(null);
  const preview = answered?.rate === rateText ? answered.value : null;
  const ask = props.preview;
  useEffect(() => {
    if (panel !== "ajuster" || ask === undefined) return;
    let current = true;
    void ask(rateText).then((value) => {
      if (current) setAnswered({ rate: rateText, value });
    });
    return () => {
      current = false;
    };
  }, [panel, rateText, ask]);
  // The reason « Valider à {taux} % » is inert, from the door's answer alone: none while it has not answered, a hard block's own
  // sentence, the justification a new gap asks for. `null` where the window has no preview door (a screen drawn without one).
  const adjustReason: string | null =
    ask === undefined
      ? null
      : props.busy
        ? t("bank.decision.envoiEnCours")
        : preview === null
          ? t("bank.decision.calculEnCours")
          : preview.blocks.length > 0
            ? t("bank.decision.blocageRaison")
            : preview.justificationRequired && note.trim() === ""
              ? t("bank.decision.ecartDemandeJustification")
              : null;
  const decided = file.stage !== "validation";
  const lines = instructions
    .split("\n")
    .map((one) => one.trim())
    .filter((one) => one !== "");
  const closePanel = (): void => {
    setPanel("none");
  };
  return (
    <div data-testid="bank-decision">
      <DecisionHead
        file={file}
        bankName={props.bankName}
        vehicleYear={props.vehicleYear}
        requestedMonths={props.requestedMonths}
        onBack={props.onBack}
      />
      {props.onComportement === undefined ? null : (
        <div className="mb-4 flex items-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={props.onComportement}
            data-testid="bank-decision-comportement"
            aria-label={`${t("bank.comportement.boutonChef")} ${t("bank.comportement.boutonChefSousTitre")}`}
          >
            {t("bank.comportement.boutonChef")}
          </Button>
          <span className="text-[length:var(--hk-fs-meta,13px)] text-[color:var(--hk-text-2)]">
            {t("bank.comportement.boutonChefSousTitre")}
          </span>
        </div>
      )}
      <DecisionTiles file={file} borrowerInsuranceRatePct={props.borrowerInsuranceRatePct} />

      <PolicyCard file={file} />

      <DecisionJustification file={file} />

      {/* `CHEF-SELF-VALIDATION` 2c: the server's own sentence for the chef who instructed this file herself, in a box ABOVE the controls; nothing is added under « Valider et envoyer au client ». */}
      {file.ownFile === null || file.ownFile === undefined ? null : (
        <Callout
          tone="info"
          className="mb-3"
          testId="bank-decision-own-file-notice"
          icon={<Info className="h-5 w-5 text-indigo-600" aria-hidden="true" />}
        >
          {file.ownFile.notice}
        </Callout>
      )}

      {props.refusal === null ? null : (
        <Callout tone="bad" className="mb-3" testId="bank-decision-refus">
          {props.refusal}
        </Callout>
      )}
      {decided ? (
        <>
          <p className="text-sm text-gray-700" data-testid="bank-decision-prise">
            {t("bank.decision.dejaPrise")}
          </p>
          {/* `CHEF-SELF-VALIDATION` 2c: the journal line of the decision taken, as served. */}
          {file.decisions.map((one) =>
            one.journalFact === null || one.journalFact === undefined ? null : (
              <p key={one.id} className="mt-1 text-sm text-gray-500" data-testid="bank-decision-journal">
                {one.journalFact}
              </p>
            ),
          )}
        </>
      ) : (
        <ActionBar label={t("bank.decision.barre")} testId="bank-decision-controles">
          <p className={`m-0 basis-full ${BANK_FS_BODY} ${BANK_TEXT_2}`}>{t("bank.decision.barreIndice")}</p>
          <span className="flex-1" />
          <Button
            variant="danger"
            disabled={props.busy}
            data-testid="bank-decision-refuser"
            onClick={() => {
              setRefuseOpen((count) => count + 1);
            }}
          >
            {t("bank.decision.refuser")}
          </Button>
          <Button
            variant="secondary"
            data-testid="bank-decision-renvoyer"
            onClick={() => {
              setPanel("renvoyer");
            }}
          >
            {t("bank.decision.renvoyer")}
          </Button>
          <Button
            variant="secondary"
            data-testid="bank-decision-ajuster"
            onClick={() => {
              setPanel("ajuster");
            }}
          >
            {t("bank.decision.ajusterTaux")}
          </Button>
          {props.policyMissing === true ? (
            // `POLICY-FIRST` Screen B, frame 167 (a defence: the door refuses the validation too, `bank-decision.controller.ts`).
            <GuardedButton
              size="lg"
              disabled
              reason={t("bank.politique.manquanteGarde")}
              {...GUARD_CHIP}
              testId="bank-decision-valider"
            >
              <Send className="mr-2 inline h-4 w-4" aria-hidden="true" />
              {t("bank.decision.validerEnvoyer")}
            </GuardedButton>
          ) : (
            <Button
              size="lg"
              disabled={props.busy}
              data-testid="bank-decision-valider"
              onClick={() => {
                props.onDecide(
                  note.trim() === "" ? { outcome: "validee" } : { outcome: "validee", note: note.trim() },
                );
              }}
            >
              <Send className="mr-2 inline h-4 w-4" aria-hidden="true" />
              {t("bank.decision.validerEnvoyer")}
            </Button>
          )}
        </ActionBar>
      )}

      {decided ? null : (
        <>
          {/* Modal 1 of 3 , « Renvoyer »: the instructions, one per line; the confirm waits for one. */}
          <Modal
            open={panel === "renvoyer"}
            title={t("bank.decision.renvoyer")}
            onClose={closePanel}
            cross={false}
            size="lg"
            actions={
              <>
                <ModalCancel testId="bank-decision-renvoyer-annuler" onClick={closePanel} />
                {lines.length === 0 ? (
                  <GuardedButton
                    disabled
                    reason={t("bank.decision.consigneMotif")}
                    testId="bank-decision-renvoyer-confirmer"
                  >
                    {t("bank.decision.renvoyerConsignes")}
                  </GuardedButton>
                ) : (
                  <Button
                    variant="ink"
                    disabled={props.busy}
                    data-testid="bank-decision-renvoyer-confirmer"
                    onClick={() => {
                      setPanel("none");
                      props.onDecide({ outcome: "renvoyee", instructions: lines });
                    }}
                  >
                    {t("bank.decision.renvoyerConsignes")}
                  </Button>
                )}
              </>
            }
          >
            <div data-testid="bank-decision-consignes">
              <label
                className="mb-1.5 block text-sm font-medium text-gray-700"
                htmlFor="bank-decision-consignes-texte"
              >
                {t("bank.decision.consignesLabel")}
              </label>
              <Textarea
                id="bank-decision-consignes-texte"
                testId="bank-decision-consignes-texte"
                rows={4}
                value={instructions}
                onChange={setInstructions}
                placeholder={t("bank.decision.consignesAide")}
              />
            </div>
          </Modal>

          {/* Modal 2 of 3 , « Ajuster le taux »: the rate stepper, the interest figure moving with it, the chef's note. */}
          <Modal
            open={panel === "ajuster"}
            title={t("bank.decision.ajusterTaux")}
            onClose={closePanel}
            cross={false}
            size="lg"
            actions={
              <>
                <ModalCancel testId="bank-decision-ajuster-annuler" onClick={closePanel} />
                {adjustReason === null ? (
                  <GuardedButton
                    variant="ink"
                    testId="bank-decision-valider-taux"
                    onClick={() => {
                      setPanel("none");
                      props.onDecide(
                        preview?.justificationRequired === true && note.trim() !== ""
                          ? { outcome: "validee", ratePct: rateText, note: note.trim() }
                          : { outcome: "validee", ratePct: rateText },
                      );
                    }}
                  >
                    {fill("bank.decision.validerA", { taux: rateLabel(rateText) })}
                  </GuardedButton>
                ) : (
                  <GuardedButton
                    variant="ink"
                    disabled
                    reason={adjustReason}
                    reasonDress="chip"
                    testId="bank-decision-valider-taux"
                  >
                    {fill("bank.decision.validerA", { taux: rateLabel(rateText) })}
                  </GuardedButton>
                )}
              </>
            }
          >
            <div data-testid="bank-decision-ajustement">
              {/* D2 (64): the sentence under the title, then the stepper ALONE (no « Taux retenu » label, no interest bar). */}
              <p className="mb-4 mt-0 text-sm text-gray-500">{t("bank.decision.arbitrageAppartient")}</p>
              <div className="my-4 flex items-center gap-5">
                <NumberStepper
                  label={t("bank.decision.tauxRetenu")}
                  decreaseLabel={t("bank.decision.tauxMoins")}
                  increaseLabel={t("bank.decision.tauxPlus")}
                  value={rate}
                  min={RATE_MIN}
                  max={RATE_MAX}
                  step={0.1}
                  format={(value) => fill("bank.decision.pct", { n: rateLabel(oneDecimalLabel(value)) })}
                  onChange={setRate}
                  testId="bank-decision-taux-pas"
                />
                {preview === null ? null : (
                  <p className="m-0 text-sm text-gray-700" data-testid="bank-decision-apercu">
                    {t("bank.decision.apercuMensualite")}{" "}
                    <strong className="font-mono font-semibold text-gray-900">
                      {formatMoney(preview.instalmentAmount)}
                    </strong>{" "}
                    · {t("bank.decision.taeg")}{" "}
                    <strong className="font-mono font-semibold text-gray-900">
                      {fill("bank.decision.pct", { n: rateLabel(preview.taegPct) })}
                    </strong>
                  </p>
                )}
              </div>
              {preview === null ? null : (
                <>
                  {preview.blocks.length === 0 ? null : (
                    <Callout
                      tone="bad"
                      className="mb-4"
                      testId="bank-decision-blocage"
                      icon={<AlertTriangle className="h-5 w-5" aria-hidden="true" />}
                    >
                      {preview.blocks.map((block) => (
                        <div key={block.code}>
                          <p className="m-0 font-semibold">
                            {fill("bank.decision.regleEliminatoire", { regle: block.rule })}
                          </p>
                          <p className="m-0">{block.detail}</p>
                        </div>
                      ))}
                    </Callout>
                  )}
                  {preview.gaps.length === 0 ? null : (
                    <Callout
                      tone="warn"
                      className="mb-4"
                      testId="bank-decision-ecart"
                      icon={<AlertTriangle className="h-5 w-5" aria-hidden="true" />}
                    >
                      {preview.gaps.map((gap) => (
                        <div key={gap.code}>
                          <p className="m-0 font-semibold">
                            {fill("bank.decision.ecartTitre", { regle: gap.rule })}
                          </p>
                          <p className="m-0">{gap.detail}</p>
                        </div>
                      ))}
                    </Callout>
                  )}
                  {preview.justificationRequired ? (
                    <>
                      <label
                        className="mb-1 block text-sm font-medium text-gray-900"
                        htmlFor="bank-decision-votre-justification"
                      >
                        {t("bank.decision.justification")}
                      </label>
                      <Textarea
                        id="bank-decision-votre-justification"
                        testId="bank-decision-votre-justification"
                        rows={3}
                        value={note}
                        onChange={setNote}
                      />
                    </>
                  ) : null}
                </>
              )}
            </div>
          </Modal>

          {/* Modal 3 of 3 , « Refuser »: the drawing's modal, over the door's list of reasons. */}
          {refuseOpen > 0 ? (
            <RefuseModal
              key={refuseOpen}
              open
              reasons={file.refusalReasons}
              busy={props.busy}
              onClose={() => {
                setRefuseOpen(0);
              }}
              onConfirm={(reasonCode) => {
                setRefuseOpen(0);
                // The chef's note is his opinion, optional since `L-3b`; the motive is the code.
                props.onDecide(
                  note.trim() === ""
                    ? { outcome: "refusee", reasonCode }
                    : { outcome: "refusee", note: note.trim(), reasonCode },
                );
              }}
            />
          ) : null}
        </>
      )}
    </div>
  );
}
